import { useLingui } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef } from "react"
import type { SlideType } from "./types"

interface Props {
  slide: SlideType
  containerClassName?: string
  /** The slide is on screen, so its video plays */
  active: boolean
  /** The slide is on screen or next, so its video downloads ahead of time */
  preload: boolean
}

export function Slide({ slide, containerClassName, active, preload }: Props) {
  const { t } = useLingui()
  const videoRef = useRef<HTMLVideoElement>(null)

  // Only the on-screen video plays. An autoplaying video downloads in full
  // whatever its preload, so offscreen slides must not autoplay.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (active) {
      video.play().catch(() => {
        // Ignore autoplay errors
      })
    } else {
      video.pause()
    }
  }, [active])

  return (
    <div className="relative w-full flex-shrink-0">
      <SlideContainer containerClassName={containerClassName}>
        {slide.backgroundType === "video" ? (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            src={slide.background}
            autoPlay={active}
            muted
            loop
            playsInline
            preload={preload ? "auto" : "none"}
          />
        ) : (
          <Image
            className="object-cover"
            fill
            src={slide.background}
            alt={`${slide.name} ${t`background`}`}
            unoptimized
            priority={slide.imageWithPriority}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60" />
        {slide.link ? (
          <Link href={slide.link} className="absolute inset-0 z-10">
            <span className="sr-only">{slide.name}</span>
          </Link>
        ) : null}
        <div className="absolute bottom-0 left-0 z-20 flex flex-col gap-1 p-4 lg:gap-2 lg:p-6">
          {slide.title}
          {slide.subtitle}
        </div>
      </SlideContainer>
    </div>
  )
}

export function SlideSkeleton({
  containerClassName,
}: { containerClassName?: string }) {
  return (
    <SlideContainer containerClassName={containerClassName}>
      <Skeleton className="h-full w-full" />
    </SlideContainer>
  )
}

function SlideContainer({
  children,
  containerClassName,
}: { children: React.ReactNode; containerClassName?: string }) {
  return (
    <div
      className={cn(
        "relative aspect-[21/5] min-h-[300px] w-full overflow-hidden md:rounded-xl",
        containerClassName,
      )}
    >
      {children}
    </div>
  )
}
